import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import { type Language } from './utils/languageUtils';

interface PrivacyPolicyProps {
  language: Language;
}

interface PrivacyTranslations {
  en: {
    title: string;
    lastUpdated: string;
    sections: Array<{
      title: string;
      content: string[];
    }>;
  };
  uk: {
    title: string;
    lastUpdated: string;
    sections: Array<{
      title: string;
      content: string[];
    }>;
  };
}

const translations: PrivacyTranslations = {
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated: October 2025',
    sections: [
      {
        title: 'Introduction',
        content: [
          'Welcome to MediSnap. We are committed to protecting your privacy and ensuring the security of your personal health information. This Privacy Policy explains how we handle your data when you use our mobile application.',
          'MediSnap is designed with privacy as a core principle. We believe your health information belongs to you and should remain under your control at all times.'
        ]
      },
      {
        title: 'Data Collection and Storage',
        content: [
          'MediSnap does not collect or share your personal data with third parties.',
          'All medication schedules, notes, images, and health-related information are stored locally on your device only.',
          'We do not have access to your medication data, schedules, or any information you enter into the app.',
          'Your data never leaves your device except when you explicitly use AI features (detailed below).'
        ]
      },
      {
        title: 'AI Processing',
        content: [
          'MediSnap uses the Rork API to provide AI-powered features for processing prescription images and generating medication schedules.',
          'When you use the AI scanning feature, only the text or image you choose to process is temporarily sent to the Rork API for analysis.',
          'The Rork API processes this information to extract medication details and returns the results to your device.',
          'This data is not stored by the API service and is only used to provide the requested functionality.',
          'You have full control over when and what information is sent for AI processing.'
        ]
      },
      {
        title: 'Data Security',
        content: [
          'You can protect your data with a PIN code or Face ID authentication.',
          'All security features are implemented locally on your device using iOS security frameworks.',
          'We do not have access to your PIN or biometric data.',
          'Your medication information is protected by your device\'s security measures.'
        ]
      },
      {
        title: 'No Tracking or Advertising',
        content: [
          'MediSnap does not track your usage, behavior, or location.',
          'We do not use any analytics, tracking pixels, or third-party advertising services.',
          'We do not sell, rent, or share your information with any third parties.',
          'The app does not contain any advertisements.'
        ]
      },
      {
        title: 'Data Deletion',
        content: [
          'You maintain complete control over your data at all times.',
          'You can delete individual medications, schedules, notes, or images within the app.',
          'To delete all data permanently, simply uninstall the app from your device.',
          'Since all data is stored locally, uninstalling the app removes all information immediately and permanently.'
        ]
      },
      {
        title: 'Children\'s Privacy',
        content: [
          'MediSnap is intended for use by adults and guardians managing medication schedules.'
        ]
      },
      {
        title: 'Changes to This Privacy Policy',
        content: [
          'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.',
          'Any changes will be posted within the app and on our website.',
          'We encourage you to review this Privacy Policy periodically.',
          'Your continued use of MediSnap after changes are posted constitutes acceptance of the updated policy.'
        ]
      },
      {
        title: 'Your Rights',
        content: [
          'Since all data is stored locally on your device, you have complete control over your information.',
          'You have the right to access, modify, or delete any data within the app at any time.',
          'You can choose whether or not to use AI-powered features that require temporary data processing.',
          'You can opt out of any optional features without affecting core app functionality.'
        ]
      },
      {
        title: 'Contact Us',
        content: [
          'If you have any questions, concerns, or requests regarding this Privacy Policy or how MediSnap handles your data, please contact us at:',
          'Email: support@medisnap.app',
          'We will respond to your inquiry as soon as possible.'
        ]
      },
      {
        title: 'Consent',
        content: [
          'By using MediSnap, you acknowledge that you have read and understood this Privacy Policy.',
          'You consent to the data handling practices described herein.',
          'If you do not agree with any part of this Privacy Policy, please do not use the app.'
        ]
      }
    ]
  },
  uk: {
    title: 'Політика конфіденційності',
    lastUpdated: 'Останнє оновлення: Жовтень 2025',
    sections: [
      {
        title: 'Вступ',
        content: [
          'Ласкаво просимо до MediSnap. Ми прагнемо захищати вашу конфіденційність та забезпечувати безпеку вашої особистої медичної інформації. Ця Політика конфіденційності пояснює, як ми обробляємо ваші дані під час використання нашого мобільного додатку.',
          'MediSnap розроблено з принципом конфіденційності як основою. Ми вважаємо, що ваша медична інформація належить вам і повинна залишатися під вашим контролем завжди.'
        ]
      },
      {
        title: 'Збір та зберігання даних',
        content: [
          'MediSnap не збирає та не передає ваші особисті дані третім сторонам.',
          'Усі графіки прийому ліків, нотатки, зображення та інформація про здоров\'я зберігаються лише локально на вашому пристрої.',
          'Ми не маємо доступу до ваших даних про ліки, графіків чи будь-якої інформації, яку ви вводите в додаток.',
          'Ваші дані ніколи не покидають ваш пристрій, окрім випадків, коли ви явно використовуєте функції AI (детальніше нижче).'
        ]
      },
      {
        title: 'Обробка за допомогою AI',
        content: [
          'MediSnap використовує Rork API для надання функцій AI для обробки зображень рецептів та генерації графіків прийому ліків.',
          'Коли ви використовуєте функцію AI-сканування, лише текст або зображення, які ви обираєте для обробки, тимчасово відправляються до Rork API для аналізу.',
          'Rork API обробляє цю інформацію для отримання деталей про ліки та повертає результати на ваш пристрій.',
          'Ці дані не зберігаються сервісом API і використовуються лише для надання запитуваної функціональності.',
          'Ви маєте повний контроль над тим, коли і яка інформація надсилається для обробки AI.'
        ]
      },
      {
        title: 'Безпека даних',
        content: [
          'Ви можете захистити свої дані за допомогою PIN-коду або аутентифікації Face ID.',
          'Всі функції безпеки реалізовані локально на вашому пристрої з використанням фреймворків безпеки iOS.',
          'Ми не маємо доступу до вашого PIN-коду або біометричних даних.',
          'Ваша інформація про ліки захищена засобами безпеки вашого пристрою.'
        ]
      },
      {
        title: 'Відсутність відстеження та реклами',
        content: [
          'MediSnap не відстежує ваше використання, поведінку або місцезнаходження.',
          'Ми не використовуємо аналітику, пікселі відстеження або сторонні рекламні сервіси.',
          'Ми не продаємо, не здаємо в оренду і не передаємо вашу інформацію будь-яким третім сторонам.',
          'Додаток не містить жодної реклами.'
        ]
      },
      {
        title: 'Видалення даних',
        content: [
          'Ви маєте повний контроль над своїми даними в будь-який час.',
          'Ви можете видалити окремі ліки, графіки, нотатки або зображення в додатку.',
          'Щоб видалити всі дані назавжди, просто видаліть додаток зі свого пристрою.',
          'Оскільки всі дані зберігаються локально, видалення додатку негайно та остаточно видаляє всю інформацію.'
        ]
      },
      {
        title: 'Конфіденційність дітей',
        content: [
          'MediSnap призначений для використання дорослими та опікунами, які керують графіками прийому ліків.',
        ]
      },
      {
        title: 'Зміни в цій Політиці конфіденційності',
        content: [
          'Ми можемо час від часу оновлювати цю Політику конфіденційності, щоб відобразити зміни в наших практиках або законодавчих вимогах.',
          'Будь-які зміни будуть опубліковані в додатку та на нашому веб-сайті.',
          'Ми рекомендуємо періодично переглядати цю Політику конфіденційності.',
          'Ваше подальше використання MediSnap після публікації змін означає прийняття оновленої політики.'
        ]
      },
      {
        title: 'Ваші права',
        content: [
          'Оскільки всі дані зберігаються локально на вашому пристрої, ви маєте повний контроль над своєю інформацією.',
          'Ви маєте право отримувати доступ, змінювати або видаляти будь-які дані в додатку в будь-який час.',
          'Ви можете обирати, використовувати чи ні функції на основі AI, які вимагають тимчасової обробки даних.',
          'Ви можете відмовитися від будь-яких додаткових функцій без впливу на основну функціональність додатку.'
        ]
      },
      {
        title: 'Зв\'яжіться з нами',
        content: [
          'Якщо у вас виникли питання, зауваження або запити щодо цієї Політики конфіденційності або того, як MediSnap обробляє ваші дані, будь ласка, зв\'яжіться з нами:',
          'Email: support@medisnap.app',
          'Ми відповімо на ваш запит якомога швидше.'
        ]
      },
      {
        title: 'Згода',
        content: [
          'Використовуючи MediSnap, ви підтверджуєте, що прочитали та зрозуміли цю Політику конфіденційності.',
          'Ви погоджуєтесь з практиками обробки даних, описаними тут.',
          'Якщо ви не погоджуєтесь з будь-якою частиною цієї Политики конфіденційності, будь ласка, не використовуйте додаток.'
        ]
      }
    ]
  }
};

function PrivacyPolicy({ language }: PrivacyPolicyProps) {
  const t = translations[language];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy">
      <Link to="/" className="back-button">
        ← {language === 'en' ? 'Back to Home' : 'Назад на головну'}
      </Link>
      <div className="privacy-content">
        <h1>{t.title}</h1>
        <p className="last-updated">{t.lastUpdated}</p>

        {t.sections.map((section, index) => (
          <section key={index} className="privacy-section">
            <h2>{section.title}</h2>
            {section.content.map((paragraph, pIndex) => (
              <p key={pIndex}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

export default PrivacyPolicy;
